<template>
    <div
        :class="{ large, small, text, disabled }"
        :style="{ backgroundColor: text ? 'transparent' : `var(--color-${color}-tint-invisible)` }"
        class="tl-tabs"
    >
        <div class="tl-tabs-wrapper">
            <div
                v-for="(item, index) in items"
                ref="itemRefs"
                :key="index"
                class="tl-tab-item"
                :class="{ active: index == model }"
                :data-testid="`tab-${kebabCase(item)}`"
                @click="open(index)"
            >
                <span
                    :style="{ color: getColor(index) }"
                    class="tl-item-text"
                >
                    {{ item }}
                </span>
            </div>
            <div class="tl-tabs-slider-wrapper">
                <div
                    ref="slider"
                    :style="{ width: getWidth(model) + 'px', transform: `translateX(${left}px)`, backgroundColor: `var(--color-${color})` }"
                    class="tl-tabs-slider"
                >
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, inject } from 'vue'
import { promiseTimeout } from '@vueuse/core'
import { getInjects } from '@/components/$tl/utils/injects'
import { kebabCase } from 'change-case'

type Props = {
    items?: string[]
    modelValue?: number
    color?: string
    large?: boolean
    small?: boolean
    text?: boolean
    disabled?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
    (e: 'update:modelValue', value: number): void
}>()

const injects = getInjects(props, inject)

const model = ref(0)
const itemRefs = ref<HTMLDivElement[]>([])

const color = computed(() => props.color ?? injects.color?.value ?? 'tl-blue')
const items = computed(() => props.items ?? injects.items?.value)
const large = computed(() => props.large ?? injects.large?.value)
const small = computed(() => props.small ?? injects.small?.value)
const value = computed(() => props.modelValue ?? injects.modelValue?.value)
const text = computed(() => props.text ?? injects.text?.value)
// const active = computed(() => itemRefs.value[model.value])
// const widths = computed(() => itemRefs.value.map((el) => el.offsetWidth))
const left = computed(() => {
    let out = 0
    const margin = text.value ? 16 : 2

    for (let i = 0; i < model.value; i += 1) {
        out = out + getWidth(i) + margin
    }

    return out
})

const getWidth = (index: number) => {
    return itemRefs.value?.[index]?.offsetWidth
}

model.value = value.value ?? 0

const open = async (index: number) => {
    if (props.disabled) return

    await promiseTimeout(10)
    model.value = index
}

const getColor = (index: number) => {
    if (text.value) {
        return (index !== model.value ? 'var(--color-black-tint-heavy)' : `var(--color-${color.value})`)
    }

    return index !== model.value ? `var(--color-${color.value})` : 'var(--color-white)'
}

watch(model, (val) => emit('update:modelValue', val))
watch(value, (val) => {
    if (val !== model.value) model.value = val
})
</script>

<style lang="sass" scoped>
.tl-tabs
    height: 45px
    border-radius: 30px
    display: flex
    padding: 5px
    .tl-tabs-wrapper
        position: relative
        width: 100%
    .tl-tab-item
        position: relative
        z-index: 3
        height: 100%
        border-radius: 30px
        display: inline-flex
        align-items: center
        padding: 0 18px
        cursor: pointer
        // margin-left: 2px
        &:last-child
            margin-left: 0px
        .tl-item-text
            color: var(--color-tl-blue)
            text-transform: uppercase
            font-family: var(--font-family-primary)
            font-size: 12px
            font-weight: var(--font-weight-semibold)
            transition-delay: 0s
            letter-spacing: 1px
        &.active
            .tl-item-text
                transition-delay: 0.1s
    &.disabled
        .tl-tab-item
            cursor: not-allowed
        .tl-item-text
            color: var(--color-black-tint-normal) !important
        .tl-tabs-slider
            background-color: var(--color-black-tint-normal) !important

    .tl-tabs-slider
        position: absolute
        z-index: 1
        height: 100%
        transition: .2s linear all
        transition-delay: 0
        bottom: 0
        left: 0
        border-radius: 30px

    &.large
        height: 60px
        padding: 8px
        .tl-tab-item
            padding: 0 24px
            .tl-item-text
                font-size: 14px
    &.small
        height: 30px
        padding: 3px
        .tl-tab-item
            padding: 0 8px
            .tl-item-text
                font-size: 11px

    &.text
        padding: 0px
        .tl-tab-item
            border-radius: 0px
            padding: 0
            margin: 0px
            margin-right: 16px
            color: var(--black-tint-heavy)
        .tl-tabs-slider-wrapper
            width: 100%
            height: 3px
            position: relative
            background-color: var(--color-gray-tint-medium)

        .tl-tabs-slider
            height: 100%

        &.large
            .tl-tabs-slider-wrapper
                height: 4px
        &.small
            .tl-tabs-slider-wrapper
                height: 2px
</style>
